import React from 'react';
import { Box, Typography, Tooltip } from '@mui/material';

interface SegmentedMeterProps {
  percentile: number;
  label: string;
  filledColor?: string;
  unfilledColor?: string;
  markerColor?: string;
}

export const SegmentedMeter: React.FC<SegmentedMeterProps> = ({ 
  percentile, 
  label,
  filledColor = '#88c9a1',
  unfilledColor = '#e6dabb',
  markerColor = '#224466'
}) => {
  const segments = Array.from({ length: 100 }, (_, i) => i + 1);

  return (
    <Box sx={{ width: '100%', mb: 4 }}>
      <Box className="d-flex justify-content-center">
        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: { xl: '1.1rem' }, mb: '1rem' }}>{label}</Typography>
      </Box>

      {/* Top Label & Indicator Text */}
      <Box className="d-flex justify-content-between align-items-end mb-1">
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '120px', ml: `calc(${percentile}% - 60px)` }}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: markerColor, lineHeight: 1.1, textAlign: 'center' }}>
            This patient<br/>{percentile}th percentile
          </Typography>
          <Typography variant="caption" sx={{ color: markerColor }}>↓</Typography>
        </Box>
      </Box>

      {/* 100 Segment Bar Container */}
      <Box sx={{ position: 'relative', display: 'flex', gap: '2px', height: { xs: 20, xl: 28 }, width: '100%', alignItems: 'center' }}>
        {segments.map((num) => {
          const isFilled = num <= percentile;
          return (
            <Tooltip title={`Percentile ${num}`} key={num} placement="top" arrow>
              <Box
                sx={{
                  flex: 1,
                  height: '100%',
                  bgcolor: isFilled ? filledColor : unfilledColor,
                  borderRadius: '1px',
                  transition: 'transform 0.2s',
                  '&:hover': {
                    transform: 'scaleY(1.3)',
                    cursor: 'pointer'
                  }
                }}
              />
            </Tooltip>
          );
        })}
        
        {/* Patient Position Marker */}
        <Box 
          sx={{
            position: 'absolute',
            left: `calc(${percentile}% + ${(percentile / 100) * 2}px)`, // offsets slightly to land exactly in the gap
            transform: 'translateX(-50%)',
            height: '140%',
            width: '4px',
            bgcolor: markerColor,
            borderRadius: '2px',
            zIndex: 2,
            boxShadow: '0 0 4px rgba(0,0,0,0.3)'
          }}
        />
      </Box>

      {/* Footer Labels */}
      <Box className="d-flex justify-content-between mt-2">
        <Typography variant="caption" color="text.secondary">Lower relative risk</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', display: { xs: 'none', sm: 'block' } }}>
          each square = 1 of 100 patients in the reference population
        </Typography>
        <Typography variant="caption" color="text.secondary">Higher relative risk</Typography>
      </Box>
    </Box>
  );
};